import { createContext, useContext, useLayoutEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { googleFontsHref, type PortalLookDto } from '@bulk/validation';
import { useI18n } from '@/lib/i18n';
import { themeColor } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { portalLookCss, portalLookTheme, type PortalLookTheme } from './portal-look';

export interface PortalLookState {
  look: PortalLookDto;
  theme: PortalLookTheme;
}

const PortalLookContext = createContext<PortalLookState | null>(null);

/** The gym's look while the sign-in pages or the member area wear it; null everywhere else. */
export function usePortalLook(): PortalLookState | null {
  return useContext(PortalLookContext);
}

const toneClass = (theme: PortalLookTheme) => (theme.tone === 'dark' ? 'theme-dark' : 'theme-light');

function PortalLookStyles({ look, page }: { look: PortalLookDto; page: boolean }) {
  const href = googleFontsHref(look.design);
  const css = useMemo(() => portalLookCss(look, { page }), [look, page]);
  return (
    <>
      {href ? <link rel="stylesheet" href={href} /> : null}
      <style>{css}</style>
    </>
  );
}

function useDocumentLook(state: PortalLookState | null, locale: string) {
  useLayoutEffect(() => {
    if (!state) return;
    const root = document.documentElement;
    const classes = ['portal-look', 'member-design', toneClass(state.theme)];
    const language = root.lang;
    root.lang = locale;
    root.classList.add(...classes);
    for (const [name, value] of Object.entries(state.theme.vars)) root.style.setProperty(name, value);
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', state.theme.background);
    return () => {
      root.lang = language;
      root.classList.remove(...classes);
      for (const name of Object.keys(state.theme.vars)) root.style.removeProperty(name);
      meta?.setAttribute('content', themeColor(root.dataset['theme'] === 'dark'));
    };
  }, [state, locale]);
}

/**
 * Dresses the whole page while `active`: the tokens go on the document element, because dialogs, menus and
 * toasts draw outside any wrapper of ours and still have to wear the gym's design.
 */
export function PortalLookProvider({ look, active, children }: { look: PortalLookDto | null; active: boolean; children: ReactNode }) {
  const state = useMemo(() => (look && active ? { look, theme: portalLookTheme(look) } : null), [look, active]);
  const { locale } = useI18n();
  useDocumentLook(state, locale);
  return (
    <PortalLookContext.Provider value={state}>
      {state ? <PortalLookStyles look={state.look} page /> : null}
      {children}
    </PortalLookContext.Provider>
  );
}

/** The same look inside one element, for a preview that sits in a page of Bulk's own. */
export function PortalLookScope({ look, className, style, children }: { look: PortalLookDto; className?: string; style?: CSSProperties; children: ReactNode }) {
  const state = useMemo(() => ({ look, theme: portalLookTheme(look) }), [look]);
  return (
    <PortalLookContext.Provider value={state}>
      <div className={cn('portal-look member-design', toneClass(state.theme), className)} style={{ ...state.theme.vars, ...style, backgroundColor: state.theme.background, color: state.theme.text } as CSSProperties}>
        <PortalLookStyles look={look} page={false} />
        {children}
      </div>
    </PortalLookContext.Provider>
  );
}
